// 날짜를 YYYY년 MM월 DD일 형태로 변환
export const formatDate = (dateString) => {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    
    // 유효한 날짜인지 확인
    if (isNaN(date.getTime())) {
      return '';
    }
    
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    
    return `${year}년 ${month.toString().padStart(2, '0')}월 ${day.toString().padStart(2, '0')}일`;
  } catch (error) {
    console.error('날짜 포맷팅 오류:', error);
    return '';
  }
};

// 숫자를 정수 형태로 변환 (.00 제거)
export const formatNumber = (number) => {
  if (number === null || number === undefined || number === '') return '0';
  
  const num = parseFloat(number);
  if (isNaN(num)) return '0';
  
  // 정수인 경우 정수로, 소수인 경우 소수점 2자리까지 표시
  return Number.isInteger(num) ? num.toString() : num.toFixed(2);
};

/**
 * 계약 평수 표시용 (DB DECIMAL·소수 보존, 불필요한 끝자리 0 제거)
 * @returns {string} 숫자만, 없거나 비숫자면 ''
 */
export const formatContractAreaSize = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const num = parseFloat(value);
  if (isNaN(num)) return '';
  if (Number.isInteger(num)) return String(num);
  return num.toFixed(2).replace(/\.?0+$/, '');
};

/** 계약 평수 + 단위, 없으면 '-' */
export const formatContractAreaSizeWithUnit = (value) => {
  const s = formatContractAreaSize(value);
  return s ? `${s}평` : '-';
};

// 통화 형태로 포맷팅 (소수점은 절삭하여 정수로 표시)
export const formatCurrency = (number) => {
  if (number === null || number === undefined || number === '') return '₩0';
  
  const num = Number(number);
  if (isNaN(num)) return '₩0'; // 숫자가 아닌 경우 0으로 표시
  
  // 소수점 절삭 (양수/음수 모두 0 방향 절삭)
  const truncated = Math.trunc(num);
  return `₩${truncated.toLocaleString()}`;
};

// 날짜를 YYYY-MM-DD 형태로 변환 (HTML date input용)
export const formatDateForInput = (dateString) => {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    
    if (isNaN(date.getTime())) {
      return '';
    }
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error('날짜 입력용 포맷팅 오류:', error);
    return '';
  }
};

// 날짜와 시간을 YYYY년 MM월 DD일 HH시 mm분 형태로 변환
export const formatDateTime = (dateString) => {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    
    // 유효한 날짜인지 확인
    if (isNaN(date.getTime())) {
      return '';
    }
    
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const hours = date.getHours();
    const minutes = date.getMinutes();
    
    return `${year}년 ${month.toString().padStart(2, '0')}월 ${day.toString().padStart(2, '0')}일 ${hours.toString().padStart(2, '0')}시 ${minutes.toString().padStart(2, '0')}분`;
  } catch (error) {
    console.error('날짜/시간 포맷팅 오류:', error);
    return '';
  }
}; 

/**
 * 소수점을 제거하고 정수로 변환
 * @param {number|string} value - 변환할 값
 * @returns {number} 정수 값
 */
export const toInteger = (value) => {
  if (value === null || value === undefined || value === '') {
    return 0;
  }
  return Math.floor(Number(value)) || 0;
};

/**
 * 소수점을 제거하고 정수로 변환 (표시용)
 * @param {number|string} value - 변환할 값
 * @returns {string} 정수 문자열
 */
export const toIntegerString = (value) => {
  return toInteger(value).toString();
}; 

// 주민등록번호 마스킹: 123456-1****** 형태
export const maskResidentNumber = (ssn) => {
  if (!ssn || typeof ssn !== 'string') return '';
  const trimmed = ssn.replace(/[^0-9-]/g, '');
  // 허용 포맷: XXXXXX-XXXXXXX 또는 XXXXXXXXXXXXX
  const digits = trimmed.replace(/-/g, '');
  if (digits.length !== 13) return ssn; // 길이 불일치 시 원문 반환
  const front = digits.slice(0, 6);
  const mid = digits.slice(6, 7);
  return `${front}-${mid}******`;
};

/** API Sequelize/검증 오류의 field 코드 → 한글 라벨 (not null 등 메시지용) */
const API_ERROR_FIELD_LABEL_KO = {
  member_number: '묘적 번호',
  complex: '단지(묘역)',
  area: '구역',
  row_number: '열',
  number: '번호',
  area_size: '크기(평)',
  period: '사용기간',
  tomb_type: '묘지 형태',
  number_of_tombs: '기수',
  relocation_date: '이장일자',
};

/**
 * axios 에러 response.data.errors — 문자열 또는 { field, message } 배열을 읽을 수 있는 문장으로 변환
 * @param {import('axios').AxiosError} error
 * @returns {string[]}
 */
export const formatApiErrorDetailLines = (error) => {
  const raw = error?.response?.data?.errors;
  if (!Array.isArray(raw) || raw.length === 0) return [];

  return raw.map((item) => {
    if (typeof item === 'string') return item;
    if (item && typeof item === 'object') {
      const field = item.field || item.path || '';
      const msg = item.message || item.msg || '';
      if (field && /cannot be null/i.test(String(msg))) {
        const label = API_ERROR_FIELD_LABEL_KO[field] || field;
        return `${label}은(는) 필수입니다.`;
      }
      if (field && msg) return `${field}: ${msg}`;
      if (msg) return msg;
    }
    try {
      return JSON.stringify(item);
    } catch {
      return String(item);
    }
  });
};

/**
 * 계약 저장(생성/수정) API 실패 시 토스트용 본문
 * @param {import('axios').AxiosError} error
 * @param {string} fallbackMessage
 */
export const formatContractSaveErrorMessage = (error, fallbackMessage) => {
  const responseData = error?.response?.data || {};
  const apiMessage = responseData?.message;
  const detailLines = formatApiErrorDetailLines(error);

  const messages = [fallbackMessage];
  if (apiMessage) messages.push(apiMessage);
  if (detailLines.length > 0) {
    messages.push(...detailLines.map((line) => `- ${line}`));
  } else if (!apiMessage && error?.message) {
    messages.push(error.message);
  }

  return messages.join('\n');
};

/** 이전 이름 호환 (eslint no-undef / 구 코드 호출부) */
export const formatSaveErrorMessage = formatContractSaveErrorMessage;

/**
 * 전자계약서(eformsign) 기수 필드 값 — mausoleum.number_of_tombs
 * 매장묘: 합장/단분/쌍분, 그 외: 숫자 기수
 */
export const formatEformTombCount = (mausoleum) => {
  const v = mausoleum?.number_of_tombs;
  if (v === null || v === undefined || v === '') return '';
  return String(v);
};